"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PopupLayout = exports.Popup = void 0;
/*
 * © 2024 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const react_1 = __importDefault(require("react"));
const styled_components_1 = require("../styled-components");
const Popper_1 = require("./Popper");
const __1 = require("..");
const Wrapper = styled_components_1.styled.section `
  color: ${({ theme }) => theme.popup.fg};
  background: ${({ theme }) => theme.popup.background};
  border-radius: ${({ theme }) => theme.radius};
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: ${({ theme }) => theme.popup.boxShadow + "," + theme.boxShadows.highlight};
  overflow: hidden;
  @media (forced-colors: active), (prefers-contrast: more) {
    // In high contrast mode, the popup needs a border. Border-color will be overridden.
    border: solid;
  }
`;
const Header = styled_components_1.styled.header `
  background: ${({ theme }) => theme.popup.headerFooterBackground};
  box-shadow: ${({ theme, flat }) => flat
    ? theme.boxShadows.highlight
    : theme.boxShadows.standard + "," + theme.boxShadows.highlight};
  border-radius: ${({ theme: { radius } }) => `${radius} ${radius} 0 0`};
  padding: ${({ flat }) => (flat ? "0 10px 0 5px" : `0 10px 0 20px`)};
  display: flex;
  flex: 0 0
    ${({ theme, flat }) => (flat ? "39px" : theme.popup.headerFooterHeight)};
  flex-wrap: nowrap;
  align-items: center;
  overflow: hidden;
`;
const Footer = styled_components_1.styled.footer `
  background: ${({ theme }) => theme.popup.headerFooterBackground};
  box-shadow: ${({ theme, flat }) => flat ? "none" : theme.boxShadows.highlight};
  border-radius: ${({ theme: { radius } }) => `0 0 ${radius} ${radius}`};
  padding: ${({ theme, flat }) => flat ? "7px 10px 10px" : theme.popup.footerGap};
  gap: ${({ theme }) => theme.popup.footerGap};
  display: flex;
  // +!! casts boolean to 0/1. Flat style requires big buttons in footer, so make it grow
  flex: ${({ flat }) => `${+!!flat} 0 48px`};
  flex-wrap: nowrap;
  align-items: center;
`;
const FooterLeft = styled_components_1.styled.div `
  display: flex;
  flex: 1 1 auto;
  gap: ${({ theme }) => theme.popup.footerGap};
  height: 28px;
  align-items: center;
`;
const FooterRight = styled_components_1.styled.div `
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.popup.footerGap};
  height: 28px;
  align-items: center;
`;
/** Spacer to put at the end if there is no footer  */
const NoFooter = styled_components_1.styled.div `
  flex: 0 0 ${({ theme }) => theme.gutter};
`;
const Body = styled_components_1.styled.section `
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: ${({ theme: { gutter } }) => `${gutter} ${gutter} 0`};
  overflow: auto;
  background: ${({ theme, flat }) => flat ? theme.popup.headerFooterBackground : "#0000"};
`;
/**
 * Standard popup component.
 *
 * The open/closed state is controlled externally, by changing the `open` prop.
 * If this popup closes itself, it will call `onClose`, which should set that external state to false.
 *
 * It adds event listeners for background click and the Escape key, either of which calls onClose.
 *
 * _👉 This means that any clickable element that opens a popup must call stopPropagation on the click event,
 * or it'll just get closed immediately._
 *
 * The popup will have a header if you supply the `title` prop, and a footer if you supply the `primaryActions` and/or `secondaryActions` prop.
 */
const Popup = ({ open = false, referenceElement, height, maxHeight, width, maxWidth, title, primaryActions, secondaryActions, onClose, flat, referencePosition, children, isGrouped = false, closeOnEscape = false, }) => {
    const { popup: { defaultWidth }, } = (0, styled_components_1.useTheme)();
    return (react_1.default.createElement(Popper_1.Popper, { open,
        referenceElement,
        height,
        maxHeight,
        width: width || defaultWidth,
        maxWidth,
        referencePosition,
        isGrouped,
        closeOnEscape,
        ...((!isGrouped || closeOnEscape) && { onClose }) },
        react_1.default.createElement(exports.PopupLayout, { title,
            primaryActions,
            secondaryActions,
            flat,
            children,
            onClose })));
};
exports.Popup = Popup;
/** Popup component style without the positioning logic */
const PopupLayout = ({ title, primaryActions, secondaryActions, flat, children, onClose, isCloseButtonDisabled = false, }) => (react_1.default.createElement(Wrapper, { className: "popup-wrapper" },
    title && (react_1.default.createElement(Header, { flat: flat },
        react_1.default.createElement(__1.Flex, { flex: "0 0 95%", noWrap: true, noOverflow: true },
            react_1.default.createElement(__1.SubHeading, { noMargin: true }, title)),
        !isCloseButtonDisabled && (react_1.default.createElement(__1.Flex, { flex: "0 0 5%", justify: "flex-end" },
            react_1.default.createElement(__1.CloseButton, { onClick: (e) => {
                    e.stopPropagation();
                    onClose === null || onClose === void 0 ? void 0 : onClose();
                } }))))),
    react_1.default.createElement(Body, { flat: flat }, children),
    primaryActions || secondaryActions ? (react_1.default.createElement(Footer, { flat: flat },
        secondaryActions && react_1.default.createElement(FooterLeft, null, secondaryActions),
        primaryActions && react_1.default.createElement(FooterRight, null, primaryActions))) : (react_1.default.createElement(NoFooter, null))));
exports.PopupLayout = PopupLayout;
//# sourceMappingURL=Popup.js.map